import BlockMedia from 'components/BlockMedia';
import ContentTextItem, {
	type HighlightTextSize,
} from 'components/ContentTextItem';
import { LayoutContainer } from 'components/Layout';
import type { ValidSitecoreLink } from 'components/SitecoreLink';
import { getThemeProps, type ThemeName } from 'design-system/themes';
import type { GTMItemListId, GTMItemListName } from 'hooks/gtm';
import type { DigizuiteAsset } from 'models/asset';
import type { ProductCard } from 'models/productCard';
import type { Field, HeadingLevel } from 'models/sitecore';
import type { JSONObject } from 'types';
import { cn } from 'utils/classNames';
import { is } from 'utils/helpers';

export interface BaseProps {
	campaignBubbleLabel?: string;
	campaignBubbleSale?: boolean;
	campaignBubbleText?: string;
	className?: string;
	eyebrow?: string;
	gtmItemListId: GTMItemListId;
	gtmItemListName: GTMItemListName;
	heading?: Field<string>;
	headingLevel?: HeadingLevel;
	highlightText?: string;
	highlightTextSize?: HighlightTextSize;
	id?: string;
	imageAlt: string | undefined;
	imageSrc: string;
	isMirrored?: boolean;
	links?: ValidSitecoreLink[];
	preventAutoplay?: boolean;
	products?: ProductCard[];
	productsRequestBody?: JSONObject;
	smallImageAlt: string | undefined;
	smallImageSrc: string | undefined;
	text?: Field<string>;
	theme?: ThemeName;
	video?: DigizuiteAsset;
	videoSmall?: DigizuiteAsset;
}

interface PropsWithProducts extends BaseProps {
	products: ProductCard[] | undefined;
}
interface PropsWithRequestBody extends BaseProps {
	productsRequestBody: JSONObject | undefined;
}

export type Props = PropsWithProducts | PropsWithRequestBody;

export default function Inspiration({
	campaignBubbleLabel,
	campaignBubbleSale,
	campaignBubbleText,
	className,
	eyebrow,
	gtmItemListId,
	gtmItemListName,
	heading,
	headingLevel = 'h2',
	highlightText,
	highlightTextSize,
	id,
	imageAlt,
	imageSrc,
	isMirrored,
	links,
	preventAutoplay,
	products,
	productsRequestBody,
	smallImageAlt,
	smallImageSrc,
	text,
	theme = 'base',
	video,
	videoSmall,
}: Props) {
	const themeProps = getThemeProps(theme);

	const media = (
		<BlockMedia
			className="col-span-full md:col-span-8"
			image={[
				{
					src: smallImageSrc ?? imageSrc,
					alt: smallImageAlt ?? imageAlt,
					variant: smallImageSrc ? 'squareSmall' : 'landscape2x1Small',
					className: 'sm:hidden',
				},
				{
					src: smallImageSrc ?? imageSrc,
					alt: smallImageAlt ?? imageAlt,
					variant: smallImageSrc ? 'square' : 'landscape2x1Small',
					className: 'md:hidden max-sm:hidden',
				},
				{
					src: imageSrc,
					alt: imageAlt,
					// Not using landscape2x1Half since the vertical stacking stays until
					// the lg breakpoint and the half size is too small for that.
					variant: 'landscape2x1',
					className: 'max-md:hidden',
				},
			]}
			video={[
				video && {
					...video,
					className: 'max-md:hidden',
				},
				videoSmall && {
					...videoSmall,
					className: 'md:hidden',
				},
			].filter(is.truthy)}
			campaignBubbleSmallSize="small"
			campaignBubbleLargeSize="medium"
			campaignBubbleLabel={campaignBubbleLabel}
			campaignBubbleSale={campaignBubbleSale}
			campaignBubbleText={campaignBubbleText}
			preventAutoplay={preventAutoplay}
			products={products}
			productsDisplayType="shopByMedia"
			productsRequestBody={productsRequestBody}
			gtmItemListId={gtmItemListId}
			gtmItemListName={gtmItemListName}
		/>
	);

	const renderText = (extraClassName?: string) => (
		<ContentTextItem
			theme={theme}
			hasThemePadding={false}
			showRibbon={false}
			eyebrow={eyebrow}
			highlightText={highlightText}
			highlightTextSize={highlightTextSize}
			headingLevel={headingLevel}
			heading={heading?.value}
			content={text?.value}
			links={links}
			className={cn(
				extraClassName,
				'col-span-full md:col-span-4',
				'max-md:pt-r-md',
				!themeProps.isPadded && 'max-md:pt-r-md',
				themeProps.isPadded && [
					'max-md:p-r-md md:py-r-md',
					isMirrored ? 'md:pl-r-md' : 'md:pr-r-md',
				],
			)}
		/>
	);

	return (
		<LayoutContainer
			id={id}
			theme={theme}
			withGrid
			outerClassName={className}
			innerClassName="items-center"
		>
			{/* The secondary text should always be below the primary part on small screens. */}
			{isMirrored && (
				<>
					{renderText('max-md:hidden')}
					{media}
					{renderText('md:hidden')}
				</>
			)}
			{!isMirrored && (
				<>
					{media}
					{renderText()}
				</>
			)}
		</LayoutContainer>
	);
}
Inspiration.displayName = 'Inspiration';
